import { ReactElement, ReactNode } from 'react'
import { GoogleTagManager } from '@next/third-parties/google'
import { NextPage } from 'next'
import { type AppContext, type AppProps, default as NextApp } from 'next/app'
import dynamic from 'next/dynamic'

import SVGSprites from '@/components/base/svg-sprites'
import { Store, StoreProvider } from '@/contexts/store/'
import { ThemeProvider } from '@/contexts/theme'
import { TransitionProvider } from '@/contexts/transition'
import useLenisPreventForIubenda from '@/hooks/useLenisPreventForIubenda'
import { mapOptionsDataProps } from '@/utils/pageData'
import { getPromiseFromFetch } from '@/utils/request'
import { API, GTM_ID } from '@/constants'

import '@/styles/globals.css'

import '@/base/gsap'

const GridHelper = dynamic(() => import('@/components/base/grid-helper'), {
  ssr: false
})

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement, props: AppProps) => ReactNode
}

export default function App({
  Component,
  pageProps,
  strings
}: AppProps & Store & { Component: NextPageWithLayout }) {
  const getLayout = Component.getLayout ?? ((page) => page)
  useLenisPreventForIubenda()

  return (
    <>
      {GTM_ID ? <GoogleTagManager gtmId={GTM_ID} /> : null}
      <StoreProvider strings={strings}>
        <ThemeProvider fluid theme={pageProps?.acf?.theme}>
          {process.env.NODE_ENV !== 'production' ? <GridHelper /> : null}
          <TransitionProvider pageProps={pageProps}>
            {getLayout(<Component {...pageProps} />, pageProps)}
          </TransitionProvider>
          <SVGSprites />
        </ThemeProvider>
      </StoreProvider>
    </>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)
  const lang = appContext.router.locale

  const { data: optionsData } = await getPromiseFromFetch({
    method: 'get',
    url: `${API.STRINGS}?lang=${lang}`
  })

  return {
    ...appProps,
    strings: await mapOptionsDataProps(optionsData)
  }
}
